/**
 * BotesSalidaDialog — Añadir o quitar botes de una salida.
 *
 * El formulario de editar salida no tiene campo de botes: la relación es de
 * muchos a muchos y vive en su propia tabla. Hasta ahora solo se podía tocar
 * desde Pasadías → Asignación de botes, que además lista únicamente salidas
 * futuras. Quien quería corregir los botes de una salida ya pasada —o
 * simplemente estaba parado en la lista de salidas— no tenía por dónde.
 *
 * Guarda cada cambio de una vez contra el servidor, sin botón de confirmar:
 * asignar y quitar son operaciones sueltas, no un formulario.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, Alert, CircularProgress, Chip, Stack,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function fmtFecha(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('es-CO',
      { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return s; }
}

export default function BotesSalidaDialog({ salida, embarcaciones = [], onClose, onCambio }) {
  const [asignados, setAsignados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!salida) return;
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas/${salida.id}/embarcaciones`,
        { headers: authHeaders() });
      setAsignados(r.ok ? await r.json() : []);
    } catch { setAsignados([]); }
    setCargando(false);
  }, [salida]);

  useEffect(() => { cargar(); }, [cargar]);

  const asignar = async (embarcacionId) => {
    setTrabajando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas/${salida.id}/embarcaciones`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ embarcacion_id: embarcacionId }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        // El backend rechaza asignar un bote que ya opera otra salida a
        // distinta hora: no hay segundo viaje. El motivo viene explicado.
        setError(b.detail || 'No se pudo asignar el bote.');
      }
      await cargar();
      onCambio?.();
    } catch { setError('Error de red.'); }
    setTrabajando(false);
  };

  const quitar = async (asignacionId) => {
    setTrabajando(true); setError(null);
    try {
      await fetch(`${API_BASE}/turismo/salidas/embarcaciones/${asignacionId}`,
        { method: 'DELETE', headers: authHeaders() });
      await cargar();
      onCambio?.();
    } catch { setError('Error de red.'); }
    setTrabajando(false);
  };

  const idsAsignados = new Set(asignados.map(a => a.embarcacion_id));
  const disponibles = embarcaciones.filter(e => !idsAsignados.has(e.id));
  const capacidad = asignados.reduce(
    (s, a) => s + Number(a.capacidad_asignada || a.capacidad_bote || 0), 0);
  const reservados = Number(salida?.asientos_reservados || 0);
  const faltaCupo = capacidad > 0 && reservados > capacidad;

  return (
    <Dialog open={!!salida} onClose={trabajando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Botes de la salida
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {fmtFecha(salida?.fecha)} · {salida?.hora_salida?.slice(0, 5) || '—'} ·{' '}
          {salida?.paquete_nombre || 'Pasadía'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>{error}</Alert>}
        {faltaCupo && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
            Hay {reservados} pasajeros reservados y los botes asignados suman {capacidad} cupos.
          </Alert>
        )}

        {cargando ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#134e4a' }}>
                Asignados ({asignados.length})
              </Typography>
              <Chip size="small" label={`${capacidad} cupos · ${reservados} reservados`}
                sx={{ fontSize: 10.5, height: 20, bgcolor: '#f1f5f9', color: '#475569' }} />
            </Stack>

            {asignados.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic', mb: 2 }}>
                Esta salida no tiene botes asignados.
              </Typography>
            ) : (
              <Stack spacing={0.75} sx={{ mb: 2 }}>
                {asignados.map(a => (
                  <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1,
                                        borderRadius: 2, bgcolor: '#e0f2fe', border: '1px solid #bae6fd' }}>
                    <DirectionsBoatIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0369a1' }}>
                        {a.embarcacion_nombre || `Bote #${a.embarcacion_id}`}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {a.capacidad_asignada || a.capacidad_bote || 0} cupos
                      </Typography>
                    </Box>
                    <IconButton size="small" disabled={trabajando}
                      onClick={() => quitar(a.id)} sx={{ color: '#ef4444' }}>
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}

            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#134e4a', mb: 1 }}>
              Disponibles
            </Typography>
            {disponibles.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic' }}>
                Todos los botes activos ya están en esta salida.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {disponibles.map(e => (
                  <Box key={e.id}
                    onClick={() => !trabajando && asignar(e.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2,
                          border: '1px dashed #e2e8f0', cursor: 'pointer',
                          '&:hover': { bgcolor: '#f0fdf4', borderColor: '#10b981' } }}>
                    <DirectionsBoatIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.nombre}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {e.capacidad_total || 0} cupos
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: '#10b981', fontWeight: 700 }}>
                      + Asignar
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={trabajando}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
